/** Staff-internal comment threads on data objects, analyses and reports (P16). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

export type Comment = components['schemas']['Comment'];

const P = (id: string) => ({ params: { path: { public_id: id } } });

export async function listComments(subject: string): Promise<Comment[]> {
  return unwrap(await api.GET('/api/comments', { params: { query: { subject } } }));
}
export async function addComment(subject: string, body: string): Promise<Comment> {
  return unwrap(await api.POST('/api/comments', { body: { subject, body } }));
}
export async function editComment(id: string, body: string): Promise<Comment> {
  return unwrap(await api.PATCH('/api/comments/{public_id}', { ...P(id), body: { body } }));
}
export async function deleteComment(id: string): Promise<void> {
  unwrap(await api.DELETE('/api/comments/{public_id}', P(id)));
}
export async function commentCounts(project: string): Promise<Record<string, number>> {
  return unwrap(await api.GET('/api/comments/counts', { params: { query: { project } } }));
}

export function useComments(subject: string) {
  return useQuery({ queryKey: ['comments', subject], queryFn: () => listComments(subject) });
}
export function useCommentCounts(project: string | undefined) {
  return useQuery({ queryKey: ['comment-counts', project], queryFn: () => commentCounts(project!), enabled: !!project });
}

/** Every change refreshes the thread and the project lists' counts. */
function useRefresh(subject: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['comments', subject] });
    void qc.invalidateQueries({ queryKey: ['comment-counts'] });
  };
}
export function useAddComment(subject: string) {
  const onSuccess = useRefresh(subject);
  return useMutation({ mutationFn: (body: string) => addComment(subject, body), onSuccess });
}
export function useEditComment(subject: string) {
  const onSuccess = useRefresh(subject);
  return useMutation({ mutationFn: ({ id, body }: { id: string; body: string }) => editComment(id, body), onSuccess });
}
export function useDeleteComment(subject: string) {
  const onSuccess = useRefresh(subject);
  return useMutation({ mutationFn: deleteComment, onSuccess });
}
